// services/favorites.js
import { supabase } from '../config/supabase';

class FavoritesService {
  /**
   * Ottiene tutti i preferiti dell'utente loggato
   * @param {string} sortBy - 'date' o 'name' per ordinare
   */
  async getUserFavorites(sortBy = 'date') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Prima assicurati che l'utente esista in users
      await this.ensureUserExists(user.id);

      let query = supabase
        .from('artist_favorites')
        .select(`
          saved_at,
          artist:artists (
            id,
            spotify_id,
            name,
            image_url,
            spotify_url,
            genres
          )
        `)
        .eq('user_id', user.id);

      // Ordina nel database
      if (sortBy === 'name') {
        query = query.order('name', { ascending: true, foreignTable: 'artists' });
      } else {
        query = query.order('saved_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('[Favorites] Error loading favorites:', error);
        return [];
      }

      return data.map(fav => ({
        ...fav.artist,
        saved_at: fav.saved_at
      }));
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return [];
    }
  }

  /**
   * Verifica se un artista è nei preferiti dell'utente
   */
  async isFavorite(artistId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('artist_favorites')
        .select('artist_id')
        .eq('user_id', user.id)
        .eq('artist_id', artistId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
        console.error('[Favorites] Error checking favorite:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return false;
    }
  }

  /**
   * Ottiene lista di artist_id preferiti (per check veloce)
   */
  async getFavoriteArtistIds() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('artist_favorites')
        .select('artist_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('[Favorites] Error loading favorite IDs:', error);
        return [];
      }

      return data.map(fav => fav.artist_id);
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return [];
    }
  }

  /**
   * Salva un artista nei preferiti
   * Se l'artista non esiste in artists, lo crea prima
   */
  async addFavorite(artistData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Assicurati che l'utente esista in users
      await this.ensureUserExists(user.id);

      // Cerca o crea l'artista in artists
      let artistId;
      if (artistData.spotify_id) {
        // Cerca per spotify_id
        const { data: existingArtist } = await supabase
          .from('artists')
          .select('id')
          .eq('spotify_id', artistData.spotify_id)
          .single();

        if (existingArtist) {
          artistId = existingArtist.id;
        } else {
          // Crea nuovo artista
          const { data: newArtist, error: insertError } = await supabase
            .from('artists')
            .insert({
              spotify_id: artistData.spotify_id,
              name: artistData.name,
              image_url: artistData.image_url || null,
              spotify_url: artistData.spotify_url || null,
              genres: artistData.genres || []
            })
            .select('id')
            .single();

          if (insertError) {
            throw insertError;
          }
          artistId = newArtist.id;
        }
      } else {
        throw new Error('spotify_id is required');
      }

      // Aggiungi ai preferiti
      const { error: favoriteError } = await supabase
        .from('artist_favorites')
        .insert({
          user_id: user.id,
          artist_id: artistId
        });

      if (favoriteError) {
        // Se è già nei preferiti, ignora
        if (favoriteError.code === '23505') {
          return { success: true, alreadyFavorite: true };
        }
        throw favoriteError;
      }

      return { success: true, artistId };
    } catch (error) {
      console.error('[Favorites] Error adding favorite:', error);
      throw error;
    }
  }

  /**
   * Rimuove un artista dai preferiti
   */
  async removeFavorite(artistId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('artist_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('artist_id', artistId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('[Favorites] Error removing favorite:', error);
      throw error;
    }
  }

  /**
   * Toggle preferito (aggiunge se non c'è, rimuove se c'è)
   */
  async toggleFavorite(artistData) {
    try {
      // Prima trova l'artist_id
      let artistId;
      if (artistData.spotify_id) {
        const { data: artist } = await supabase
          .from('artists')
          .select('id')
          .eq('spotify_id', artistData.spotify_id)
          .single();

        if (!artist) {
          // Se non esiste, aggiungilo (sarà creato in addFavorite)
          return await this.addFavorite(artistData);
        }
        artistId = artist.id;
      } else {
        throw new Error('spotify_id is required');
      }

      // Verifica se è già preferito
      const isFav = await this.isFavorite(artistId);
      
      if (isFav) {
        return await this.removeFavorite(artistId);
      } else {
        return await this.addFavorite(artistData);
      }
    } catch (error) {
      console.error('[Favorites] Error toggling favorite:', error);
      throw error;
    }
  }

  /**
   * Assicura che l'utente esista nella tabella users
   */
  async ensureUserExists(userId) {
    try {
      const { error } = await supabase
        .from('users')
        .insert({ user_id: userId })
        .select()
        .single();

      // Se errore è "already exists", va bene
      if (error && error.code !== '23505') {
        // Se è un altro errore, potrebbe essere che esiste già (ignora)
        if (error.code !== 'PGRST116') {
          console.warn('[Favorites] User might already exist:', error.message);
        }
      }
    } catch (error) {
      // Ignora errori di duplicato
    }
  }
}

export default new FavoritesService();

